/**
 * @file BlockWrapper hooks，包含块/组移动和复制操作
 */
import { Toast } from '@seiue/ui'
import { cloneDeep } from '@seiue/util'
import { RefObject, useContext, useEffect } from 'react'

import {
  blockComparator,
  getChildrenBlocks,
  rearrangeBlocks,
} from 'packages/feature-utils/test-paper'
import { PaperBlock, paperApi } from 'packages/sdks-next/growp'

import { HandoutBlockListContext } from '../BlockList/context'

const MOUSEDOWN = 'mousedown'
const TOUCHSTART = 'touchstart'
type HandledEvents = [typeof MOUSEDOWN, typeof TOUCHSTART]
type HandledEventsType = HandledEvents[number]
type PossibleEvent = {
  [Type in HandledEventsType]: HTMLElementEventMap[Type]
}[HandledEventsType]
type Handler = (event: PossibleEvent) => void

/**
 * 点击 ref 外触发 handler 事件
 *
 * @param ref - HTMLElement 的 RefObject
 * @param handler - 触发的事件
 * @param boundry - 事件绑定的边界，即在 ref 外，boundry 内触发 handler，默认为 documentElement
 */
export const useClickOutside = (
  ref: RefObject<HTMLElement>,
  handler: Handler | null,
  boundry: HTMLElement | null
) => {
  const boundryElement = boundry || document.documentElement

  useEffect(
    () => {
      const listener = (event: PossibleEvent) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return
        }

        handler?.(event)
      }

      boundryElement.addEventListener('mousedown', listener)
      boundryElement.addEventListener('touchstart', listener)
      return () => {
        boundryElement.removeEventListener('mousedown', listener)
        boundryElement.removeEventListener('touchstart', listener)
      }
    },
    /*
     * Add ref and handler to effect dependencies
     * It's worth noting that because passed in handler is a new ...
     * ... function on every render that will cause this effect ...
     * ... callback/cleanup to run every render. It's not a big deal ...
     * ... but to optimize you can wrap handler in useCallback before ...
     * ... passing it into this hook.
     */
    [ref, handler, boundryElement]
  )
}

/**
 * 移动
 *
 * @param blockData - block data
 * @param blocks - blocks
 * @returns function
 */
export const useMove = (blockData: PaperBlock, blocks: PaperBlock[]) => {
  // FIXME 上移下移组问题待修复
  const {
    api: { batchUpdateBlock },
  } = useContext(HandoutBlockListContext)

  const canMoveDown = blockData.weight !== blocks.length
  const canMoveUp = blockData.weight !== 1

  const onMove = async (direction: 'up' | 'down', range: 'block' | 'group') => {
    const currentBlockWeight = blockData?.weight || 0

    let childrenBlocks = []
    if (range === 'group') {
      childrenBlocks = getChildrenBlocks(blockData.id, blocks || [])
    } else {
      childrenBlocks = [blockData]
    }

    const offset =
      direction === 'up' ? -childrenBlocks.length : childrenBlocks.length

    const block = (blocks || []).find(
      (b) =>
        b.weight ===
        (direction === 'up'
          ? currentBlockWeight - 1
          : currentBlockWeight + childrenBlocks.length)
    )

    if (block) {
      const tobeUpdate = [
        {
          ...block,
          weight: (block.weight || 0) - offset,
        },
        ...childrenBlocks.map((b) => ({
          ...b,
          weight: (b.weight || 0) + (direction === 'up' ? -1 : 1),
        })),
      ]

      await batchUpdateBlock(tobeUpdate)

      Toast.success('移动成功')
    }
  }

  return {
    canMoveUp,
    canMoveDown,
    onMove,
  }
}

/**
 * 复制
 *
 * @param blockData - blockData
 * @param blocks - blocks
 * @returns function
 */
export const useDuplicate = (blockData: PaperBlock, blocks: PaperBlock[]) => {
  const {
    paper,
    api: { batchUpdateBlock },
  } = useContext(HandoutBlockListContext)

  const paperId = paper.id
  const { api: copyBlock } = paperApi.useCopyBlock({
    reload: [],
  })
  const onDuplicate = async (options: { range: 'block' | 'group' }) => {
    // 1. 获取复制到的块
    const { data } = await copyBlock(blockData.id)

    const offset = getChildrenBlocks(blockData.id, blocks || []).length
    // 2. 取当前块所处的位置并将复制得到的块插入块/组下方
    const sortedBlocks = blocks.sort(blockComparator)
    const index = sortedBlocks.findIndex((b) => b.id === blockData.id)
    const blocksCopy = cloneDeep(sortedBlocks)
    blocksCopy.splice(index + offset, 0, ...data.sort(blockComparator))
    // 3. 计算需要更新 weight 和 parentId 块并更新
    const tobeUpdate = rearrangeBlocks(blocksCopy)
    if (tobeUpdate.length) {
      await batchUpdateBlock(tobeUpdate)

      Toast.success('复制成功')
    }
  }

  return {
    onDuplicate,
  }
}
